import { Spinner } from '@heroui/react';
import type { IStreamVideo } from '@/shared/types/common';
import { isVideoUploadInterrupted, isVideoUploadResumable } from '../lib/videoState';

interface StepVideoServerStatusProps {
  video: IStreamVideo;
}

/**
 * Видео шага, которое ещё не готово, по данным сервера: обработка в Stream, незавершённая
 * загрузка или отказ Stream. Готовое видео показывает плеер, идущую с этого компьютера загрузку —
 * StepVideoUploadStatus.
 */
export const StepVideoServerStatus = ({ video }: StepVideoServerStatusProps) => {
  if (video.status === 'error') {
    return (
      <div className="flex flex-col gap-1 rounded-lg bg-gray-50 p-4 text-sm">
        <p className="font-medium text-red-tertiary">
          Cloudflare Stream rejected “{video.name}”. Upload another file.
        </p>
        {video.errorReason && <p className="text-grey-secondary">{video.errorReason}</p>}
      </div>
    );
  }

  if (video.status !== 'pending') {
    return null;
  }

  if (isVideoUploadInterrupted(video)) {
    return (
      <div className="flex flex-col gap-1 rounded-lg bg-gray-50 p-4 text-sm">
        <p className="font-medium text-black-headline">
          The upload of “{video.name}” has not finished
        </p>
        <p className="text-grey-secondary">
          It may still be running in another tab or browser.{' '}
          {isVideoUploadResumable(video)
            ? 'If it was interrupted, choose the same file again and the upload continues from where it stopped; a different file replaces the video.'
            : 'If it was interrupted, upload the file again or remove the video.'}{' '}
          Students do not see this step&apos;s video until it is ready.
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-4 text-sm">
      <Spinner size="sm" color="default" />
      <div className="flex flex-col gap-1">
        <p className="font-medium text-black-headline" role="status">
          Processing “{video.name}”
          {typeof video.pctComplete === 'number' ? ` — ${video.pctComplete}%` : '...'}
        </p>
        <p className="text-grey-secondary">
          The video will appear in the session when processing is finished. You can close this window
        </p>
      </div>
    </div>
  );
};
